/**
 * @fileoverview Componente responsável por criar e editar mensagens rápidas.
 */

import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { Plus, Pencil, Paperclip, X, Trash2 } from "lucide-react";
import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import ButtonWithSpinner from "../ButtonWithSpinner";

import {
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    FormControlLabel,
    Checkbox,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";

const path = require('path');

const useStyles = makeStyles((theme) => {
    const dark = theme.palette.type === "dark";
    return {
        root: { display: "flex", flexWrap: "wrap" },
        dialog: {
            "& .MuiDialog-paper": {
                fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
                width: "min(480px, calc(100vw - 32px))",
                maxWidth: "calc(100vw - 32px)",
                borderRadius: 24,
                overflow: "hidden",
                background: dark
                    ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
                    : "#ffffff",
                boxShadow: dark
                    ? "0 24px 80px rgba(0, 0, 0, 0.45)"
                    : "0 24px 60px rgba(15, 23, 42, 0.12)",
                border: dark
                    ? "1px solid rgba(255, 255, 255, 0.08)"
                    : "1px solid rgba(15, 23, 42, 0.08)",
                "& *": {
                    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
                },
            },
            "& .MuiBackdrop-root": {
                backgroundColor: dark ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.4)",
            },
        },
        modalHeader: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: theme.spacing(2.5, 3),
            borderBottom: dark
                ? "1px solid rgba(255, 255, 255, 0.06)"
                : "1px solid rgba(15, 23, 42, 0.08)",
        },
        modalTitle: {
            fontSize: "1.35rem",
            fontWeight: 700,
            color: dark ? "#f8fafc" : "#111827",
        },
        modalClose: {
            color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
        },
        textField: {
            "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                background: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                border: dark
                    ? "1px solid rgba(91, 124, 255, 0.15)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "& fieldset": { borderColor: "transparent" },
                "&:hover": {
                    background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(61, 92, 253, 0.04)",
                    border: dark
                        ? "1px solid rgba(91, 124, 255, 0.25)"
                        : "1px solid rgba(61, 92, 253, 0.15)",
                },
                "&.Mui-focused": {
                    background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 1)",
                    border: dark
                        ? "1px solid rgba(91, 124, 255, 0.4)"
                        : "1px solid rgba(61, 92, 253, 0.3)",
                    boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)",
                },
            },
        },
        multFieldLine: {
            display: "flex",
            "& > *:not(:last-child)": { marginRight: theme.spacing(1) },
        },

        // ========= Footer / Actions =========
        dialogActions: {
            padding: theme.spacing(2, 3),
            borderTop: dark
                ? "1px solid rgba(255, 255, 255, 0.06)"
                : "1px solid rgba(15, 23, 42, 0.08)",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: theme.spacing(1),
        },
        actionsPrimaryRow: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: theme.spacing(1),
            flexWrap: "wrap",
        },
        actionsCancelRow: {
            display: "flex",
            justifyContent: "center",
        },

        btnBase: {
            height: 38,
            padding: "0 16px",
            borderRadius: 10,
            fontSize: "0.875rem",
            fontWeight: 500,
            textTransform: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            border: "none",
            boxShadow: "none",
            transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
            "& svg": { flexShrink: 0 },
            "& .MuiButton-label": {
                whiteSpace: "nowrap",
            },
        },

        btnCancel: {
            background: "transparent",
            border: dark
                ? "1px solid rgba(255, 255, 255, 0.12)"
                : "1px solid rgba(15, 23, 42, 0.12)",
            color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
            "&:hover": {
                background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
                color: dark ? "#f8fafc" : "#111827",
            },
        },

        btnAttach: {
            background: dark ? "rgba(245, 158, 11, 0.12)" : "rgba(245, 158, 11, 0.08)",
            border: dark
                ? "1px solid rgba(245, 158, 11, 0.35)"
                : "1px solid rgba(245, 158, 11, 0.25)",
            color: dark ? "#fbbf24" : "#b45309",
            "&:hover": {
                background: dark ? "rgba(245, 158, 11, 0.2)" : "rgba(245, 158, 11, 0.14)",
                borderColor: dark ? "rgba(245, 158, 11, 0.55)" : "rgba(245, 158, 11, 0.45)",
            },
        },

        btnPrimary: {
            background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
            color: "#ffffff",
            "&:hover": {
                background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
            },
            "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
        },

        // Anexo já selecionado (linha mostrando o arquivo)
        attachmentRow: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            padding: theme.spacing(1, 1.25),
            borderRadius: 12,
            background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(15, 23, 42, 0.03)",
            border: dark
                ? "1px solid rgba(255, 255, 255, 0.06)"
                : "1px solid rgba(15, 23, 42, 0.06)",
        },
        attachmentIcon: {
            color: dark ? "#fbbf24" : "#b45309",
            flexShrink: 0,
        },
        attachmentName: {
            flex: 1,
            fontSize: "0.875rem",
            color: dark ? "#f8fafc" : "#1e293b",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        attachmentDelete: {
            color: dark ? "#fca5a5" : "#ef4444",
            padding: 6,
        },

        spinner: { color: "#ffffff" },
    };
});

const QuickeMessageSchema = Yup.object().shape({
    shortcode: Yup.string()
        .trim()
        .required(i18n.t("quickMessages.validation.shortcodeRequired")),
    message: Yup.string()
        .trim()
        .required(i18n.t("quickMessages.validation.messageRequired")),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const { profile } = user;
    const messageInputRef = useRef();

    const initialState = {
        shortcode: "",
        message: "",
        geral: false,
        status: true,
        isShared: false,
    };

    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [quickemessage, setQuickemessage] = useState(initialState);
    const [attachment, setAttachment] = useState(null);
    const attachmentFile = useRef(null);

    useEffect(() => {
        try {
            (async () => {
                if (!quickemessageId) return;

                const { data } = await api.get(`/quick-messages/${quickemessageId}`);

                setQuickemessage((prevState) => {
                    return { ...prevState, ...data };
                });
            })();
        } catch (err) {
            toastError(err);
        }
    }, [quickemessageId, open]);

    const handleClose = () => {
        setQuickemessage(initialState);
        setAttachment(null);
        onClose();
    };

    const handleAttachmentFile = (e) => {
      
        const file = head(e.target.files);
        if (file) {
            setAttachment(file);
        }
    };

    const handleSaveQuickeMessage = async (values) => {

        const quickemessageData = { ...values, isMedia: true, mediaPath: attachment ? String(attachment.name).replace(/ /g, "_") : values.mediaPath ? path.basename(values.mediaPath).replace(/ /g, "_") : null };

        try {
            if (quickemessageId) {
                await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
                if (attachment != null) {
                    const formData = new FormData();
                    formData.append("typeArch", "quickMessage");
                    formData.append("file", attachment);
                    await api.post(
                        `/quick-messages/${quickemessageId}/media-upload`,
                        formData
                    );
                }
            } else {
                const { data } = await api.post("/quick-messages", quickemessageData);
                if (attachment != null) {
                    const formData = new FormData();
                    formData.append("typeArch", "quickMessage");
                    formData.append("file", attachment);
                    await api.post(`/quick-messages/${data.id}/media-upload`, formData);
                }
            }
            toast.success(i18n.t("quickMessages.toasts.success"));
            if (typeof reload == "function") {

                reload();
            }
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const deleteMedia = async () => {
        if (attachment) {
            setAttachment(null);
            attachmentFile.current.value = null;
        }

        if (quickemessage.mediaPath) {
            await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
            setQuickemessage((prev) => ({
                ...prev,
                mediaPath: null,
            }));
            toast.success(i18n.t("quickMessages.toasts.deleted"));
            if (typeof reload == "function") {

                reload();
            }
        }
    };

    const handleClickMsgVar = async (msgVar, setValueFunc) => {
        const el = messageInputRef.current;
        const firstHalfText = el.value.substring(0, el.selectionStart);
        const secondHalfText = el.value.substring(el.selectionEnd);
        const newCursorPos = el.selectionStart + msgVar.length;

        setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

        await new Promise(r => setTimeout(r, 100));
        messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
    };

    return (
        <div className={classes.root}>
            <ConfirmationModal
                title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
                open={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
                onConfirm={deleteMedia}
            >
                {i18n.t("quickMessages.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                scroll="paper"
                className={classes.dialog}
            >
                <div className={classes.modalHeader}>
                    <Typography className={classes.modalTitle}>
                        {quickemessageId
                            ? i18n.t("quickMessages.dialog.edit")
                            : i18n.t("quickMessages.dialog.add")}
                    </Typography>
                    <IconButton size="small" onClick={handleClose} className={classes.modalClose}>
                        <X size={18} strokeWidth={1.75} />
                    </IconButton>
                </div>
                <div style={{ display: "none" }}>
                    <input
                        type="file"
                        ref={attachmentFile}
                        onChange={(e) => handleAttachmentFile(e)}
                    />
                </div>
                <Formik
                    initialValues={quickemessage}
                    enableReinitialize={true}
                    validationSchema={QuickeMessageSchema}
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveQuickeMessage(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ errors, isSubmitting, setFieldValue, values, submitCount }) => (
                        <Form>
                            <DialogContent dividers>
                                <Grid spacing={2} container>
                                    <Grid xs={12} item>
                                        <Field
                                            as={TextField}
                                            autoFocus
                                            label={i18n.t("quickMessages.dialog.shortcode")}
                                            name="shortcode"
                                            error={submitCount > 0 && Boolean(errors.shortcode)}
                                            helperText={submitCount > 0 ? errors.shortcode : ""}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            className={classes.textField}
                                        />
                                    </Grid>
                                    <Grid xs={12} item>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("quickMessages.dialog.message")}
                                            name="message"
                                            inputRef={messageInputRef}
                                            error={submitCount > 0 && Boolean(errors.message)}
                                            helperText={submitCount > 0 ? errors.message : ""}
                                            variant="outlined"
                                            margin="dense"
                                            multiline={true}
                                            rows={7}
                                            fullWidth
                                            className={classes.textField}
                                        // disabled={quickemessage.mediaPath || attachment ? true : false}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <MessageVariablesPicker
                                            disabled={isSubmitting}
                                            onClick={value => handleClickMsgVar(value, setFieldValue)}
                                        />
                                    </Grid>
                                    {(quickemessage.mediaPath || attachment) && (
                                        <Grid xs={12} item>
                                            <div className={classes.attachmentRow}>
                                                <Paperclip
                                                    size={16}
                                                    strokeWidth={1.75}
                                                    className={classes.attachmentIcon}
                                                />
                                                <span className={classes.attachmentName}>
                                                    {attachment ? attachment.name : quickemessage.mediaName}
                                                </span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setConfirmationOpen(true)}
                                                    className={classes.attachmentDelete}
                                                    title="Remover anexo"
                                                >
                                                    <Trash2 size={16} strokeWidth={1.75} />
                                                </IconButton>
                                            </div>
                                        </Grid>
                                    )}
                                </Grid>
                            </DialogContent>
                            <DialogActions className={classes.dialogActions}>
                                <div className={classes.actionsPrimaryRow}>
                                    {!attachment && !quickemessage.mediaPath && (
                                        <Button
                                            onClick={() => attachmentFile.current.click()}
                                            disabled={isSubmitting}
                                            className={`${classes.btnBase} ${classes.btnAttach}`}
                                            startIcon={<Paperclip size={16} strokeWidth={1.75} />}
                                        >
                                            {i18n.t("quickMessages.buttons.attach")}
                                        </Button>
                                    )}

                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`${classes.btnBase} ${classes.btnPrimary}`}
                                        startIcon={
                                            isSubmitting ? (
                                                <CircularProgress size={14} className={classes.spinner} />
                                            ) : quickemessageId ? (
                                                <Pencil size={16} strokeWidth={1.75} />
                                            ) : (
                                                <Plus size={16} strokeWidth={1.75} />
                                            )
                                        }
                                    >
                                        {quickemessageId
                                            ? i18n.t("quickMessages.buttons.edit")
                                            : i18n.t("quickMessages.buttons.add")}
                                    </Button>
                                </div>

                                <div className={classes.actionsCancelRow}>
                                    <Button
                                        onClick={handleClose}
                                        disabled={isSubmitting}
                                        className={`${classes.btnBase} ${classes.btnCancel}`}
                                        startIcon={<X size={16} strokeWidth={1.75} />}
                                    >
                                        {i18n.t("quickMessages.buttons.cancel")}
                                    </Button>
                                </div>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default QuickMessageDialog;
